import { Component, ElementRef, Input, OnInit, ViewChild, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Globals } from 'src/app/services/globals';

import '@interactjs/auto-start';
import '@interactjs/actions/drag';
import '@interactjs/actions/resize';
import '@interactjs/modifiers';
import Interact from '@interactjs/interact';
import { posterSizesByName, canvasSizesByName } from '../../../../../../../common/common-types/poster-size';

export type WizardStep = 'upload' | 'align';

export interface ImageAlignment {
    xOffset: number;
    yOffset: number;
    scale: number;
}

@Component({
    selector: 'image-upload-wizard',
    templateUrl: './image-upload-wizard.component.html',
    styleUrls: ['./image-upload-wizard.component.scss'],
    standalone: false
})
export class ImageUploadWizardComponent implements OnInit, AfterViewInit {
    @Input() productId = '';
    @Input() storeId = '';
    /** A poster size string like "30x40" */
    @Input() size = '30x40';

    @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
    @ViewChild('dragAreaElement') dragAreaElement?: ElementRef<HTMLDivElement>;
    @ViewChild('sizeBorderElement') sizeBorderElement?: ElementRef<HTMLDivElement>;
    @ViewChild('posterImageElement') posterImageElement?: ElementRef<HTMLImageElement>;

    step: WizardStep = 'upload';
    isDragOver = false;
    selectedFile: File | null = null;
    imagePreviewUrl: SafeUrl | null = null;
    rawPreviewUrl: string | null = null;

    alignment: ImageAlignment = { xOffset: 0, yOffset: 0, scale: 1 };
    scale = 1;
    minScale = 1;
    maxScale = 3;

    imageOrientation: 'portrait' | 'landscape' = 'portrait';
    availableSizes = { ...posterSizesByName, ...canvasSizesByName };

    uploading = false;
    uploadError: string | null = null;
    confirmError: string | null = null;

    private interactInstance: any = null;

    constructor(
        private sanitizer: DomSanitizer,
        private http: HttpClient,
        public globals: Globals,
    ) { }

    ngOnInit() {
        // Read from window globals set by EJS template
        if ((window as any).__ACE_PRODUCT_ID__) this.productId = (window as any).__ACE_PRODUCT_ID__;
        if ((window as any).__ACE_STORE_ID__) this.storeId = (window as any).__ACE_STORE_ID__;
        if ((window as any).__ACE_SIZE__) this.size = (window as any).__ACE_SIZE__;
        if (!this.size || !this.availableSizes[this.size]) this.size = '30x40';
        console.log('[ACE Print Partner Wizard] Initialized with selected size:', this.size);
    }

    ngAfterViewInit() { }

    // -------------------------------------------------------------------------
    // Step 1 – File selection
    // -------------------------------------------------------------------------

    onDragOver(e: DragEvent) {
        e.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(e: DragEvent) {
        this.isDragOver = false;
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        this.isDragOver = false;
        const file = e.dataTransfer?.files?.[0];
        if (file) this.selectFile(file);
    }

    onFileChange(e: Event) {
        const input = e.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (file) this.selectFile(file);
    }

    openFilePicker() {
        this.fileInput?.nativeElement.click();
    }

    private selectFile(file: File) {
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            this.uploadError = 'Please select a JPEG or PNG image file.';
            return;
        }
        this.uploadError = null;
        this.selectedFile = file;
        const reader = new FileReader();
        reader.onload = (ev) => {
            this.rawPreviewUrl = ev.target?.result as string;
            this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.rawPreviewUrl);

            // Determine image orientation from natural dimensions
            const img = new Image();
            img.onload = () => {
                this.imageOrientation = img.naturalWidth >= img.naturalHeight ? 'landscape' : 'portrait';
                // Proceed immediately to alignment once image is loaded
                this.proceedToAlign();
            };
            img.src = this.rawPreviewUrl;
        };
        reader.readAsDataURL(file);
    }

    proceedToAlign() {
        if (!this.selectedFile) return;
        this.step = 'align';
        this.scale = 1;
        this.alignment = { xOffset: 0, yOffset: 0, scale: 1 };
        setTimeout(() => this.initDrag(), 150); // Reduced timeout to feel snappier
    }

    backToUpload() {
        this.step = 'upload';
        this.selectedFile = null;
        this.imagePreviewUrl = null;
        this.rawPreviewUrl = null;
        this.destroyDrag();
    }

    // -------------------------------------------------------------------------
    // Step 2 – Alignment (drag + scale)
    // -------------------------------------------------------------------------

    private initDrag() {
        const imgEl = this.posterImageElement?.nativeElement;
        const borderEl = this.sizeBorderElement?.nativeElement;
        const areaEl = this.dragAreaElement?.nativeElement;
        if (!imgEl || !borderEl || !areaEl) return;

        // Center image initially
        const overflow_x = imgEl.getBoundingClientRect().width * this.scale - borderEl.getBoundingClientRect().width;
        const overflow_y = imgEl.getBoundingClientRect().height * this.scale - borderEl.getBoundingClientRect().height;
        const tx = borderEl.getBoundingClientRect().x - areaEl.getBoundingClientRect().x - overflow_x / 2;
        const ty = borderEl.getBoundingClientRect().y - areaEl.getBoundingClientRect().y - overflow_y / 2;

        this.applyTransform(imgEl, tx, ty, this.scale);

        const _this = this;
        let interact: any = Interact;

        // Destroy previous instance if any
        this.destroyDrag();

        this.interactInstance = interact('.ace-wizard-drag-area .wizard-poster-image').draggable({
            inertia: true,
            modifiers: [],
            autoScroll: false,
            listeners: {
                move(event: any) {
                    const target = event.target;
                    const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                    const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
                    _this.clampAndApply(target, x, y, _this.scale);
                },
                end(event: any) {
                    const target = event.target;
                    _this.saveAlignmentFromElement(target);
                },
            },
        });
    }

    private destroyDrag() {
        if (this.interactInstance) {
            try { this.interactInstance.unset(); } catch { }
            this.interactInstance = null;
        }
    }

    onScaleChange(value: number) {
        this.scale = value;
        const imgEl = this.posterImageElement?.nativeElement;
        if (!imgEl) return;
        const x = parseFloat(imgEl.getAttribute('data-x') || '0');
        const y = parseFloat(imgEl.getAttribute('data-y') || '0');
        this.clampAndApply(imgEl, x, y, this.scale);
        this.saveAlignmentFromElement(imgEl);
    }

    private clampAndApply(target: HTMLElement, rawX: number, rawY: number, scale: number) {
        const borderEl = this.sizeBorderElement?.nativeElement;
        const areaEl = this.dragAreaElement?.nativeElement;
        if (!borderEl || !areaEl) {
            this.applyTransform(target, rawX, rawY, scale);
            return;
        }

        const borderRect = borderEl.getBoundingClientRect();
        const areaRect = areaEl.getBoundingClientRect();

        // Natural (unscaled) image dimensions
        const naturalW = (target as HTMLImageElement).naturalWidth || target.getBoundingClientRect().width;
        const naturalH = (target as HTMLImageElement).naturalHeight || target.getBoundingClientRect().height;
        const scaledW = naturalW * scale;
        const scaledH = naturalH * scale;

        // Border position relative to drag area
        const borderOffsetX = borderRect.x - areaRect.x;
        const borderOffsetY = borderRect.y - areaRect.y;

        // Clamp so image always covers the border
        const minX = borderOffsetX - (scaledW - borderRect.width);
        const maxX = borderOffsetX;
        const minY = borderOffsetY - (scaledH - borderRect.height);
        const maxY = borderOffsetY;

        const clampedX = Math.max(minX, Math.min(maxX, rawX));
        const clampedY = Math.max(minY, Math.min(maxY, rawY));

        this.applyTransform(target, clampedX, clampedY, scale);
    }

    private applyTransform(target: HTMLElement, x: number, y: number, scale: number) {
        target.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        target.setAttribute('data-x', String(x));
        target.setAttribute('data-y', String(y));
    }

    private saveAlignmentFromElement(target: HTMLElement) {
        const borderEl = this.sizeBorderElement?.nativeElement;
        if (!borderEl) return;

        const imgRect = target.getBoundingClientRect();
        const borderRect = borderEl.getBoundingClientRect();

        // Calculate xAlignment relative to the left edge of the poster area
        const xAlignment = (imgRect.x - borderRect.x) / borderRect.width;

        // Calculate yAlignment relative to the BOTTOM edge of the poster area
        // If the image bottom is below the poster bottom, this offset will be negative,
        // which perfectly matches pdf-lib's bottom-left origin coordinate system.
        const yAlignment = (borderRect.bottom - imgRect.bottom) / borderRect.height;

        this.alignment = {
            xOffset: xAlignment,
            yOffset: yAlignment,
            scale: this.scale,
        };

        console.log('[ACE Print Partner Wizard] Alignment saved:', {
            alignment: this.alignment,
            scale: this.scale
        });
    }

    // -------------------------------------------------------------------------
    // Confirm – upload image and post message to parent
    // -------------------------------------------------------------------------

    async confirm() {
        if (!this.selectedFile || !this.storeId) return;
        this.uploading = true;
        this.confirmError = null;

        try {
            // Upload image to server
            const formData = new FormData();
            formData.append('image', this.selectedFile);

            const uploadRes = await fetch(`/api/stores/public/${this.storeId}/products/customer-image-upload`, {
                method: 'POST',
                body: formData,
            });

            if (!uploadRes.ok) {
                throw new Error(`Upload failed: ${uploadRes.status}`);
            }

            const { url } = await uploadRes.json();

            // Send result to parent storefront page via postMessage
            window.parent.postMessage({
                type: 'ace:imageUploadConfirmed',
                uploadedImageUrl: url,
                alignment: { ...this.alignment, scale: this.scale },
                productId: this.productId,
            }, '*');

        } catch (e: any) {
            console.error('Image upload error:', e);
            this.confirmError = 'Could not upload image. Please try again.';
        } finally {
            this.uploading = false;
        }
    }

    close() {
        window.parent.postMessage({ type: 'ace:imageUploadCancelled' }, '*');
    }
}
